/**
 * Remote on/off flag from one `app_config` row, for the current user.
 *
 * Row value (JSON text): { "enabled": true, "rollout_pct": 25, "qa_user_ids": ["<uuid>"] }
 *
 * Order of decisions:
 * 1. A user id in qa_user_ids is on, even when the flag is disabled, so QA
 *    can test before launch.
 * 2. enabled false (or a missing, malformed or unreadable row) is off.
 * 3. Otherwise the user is on when their stable bucket (0-99, a hash of flag
 *    key and user id) is below rollout_pct. 100 is on for everyone, signed
 *    out included; anything lower needs a signed-in user.
 *
 * While the row loads the flag is off and `isLoading` is true. A row whose
 * answer depends on who is signed in (a QA list, or a rollout between 0 and
 * 100) is also loading on the server and in the hydration render, since
 * neither knows the member. After that the browser decides at once from the
 * stored session while auth starts up. Pages that would show the wrong plan
 * must hold a neutral state while `isLoading` is true.
 *
 * The server reads the rows in SSR_REMOTE_FLAG_KEYS and puts them in the
 * page (seedRemoteFlagCache), so an SSR page knows the answer on first paint.
 * The key must be registered in src/config/flags.ts.
 */
import { useSyncExternalStore } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { readStoredAuthUserId } from '@/utils/productAnalyticsConsent';
import type { FlagKey } from '@/config/flags';

/** Rows api/ssr.ts reads for every page. Keep in step with SSR_REMOTE_FLAG_KEYS there. */
export const SSR_REMOTE_FLAG_KEYS = ['vip_paywall_web', 'pro_checkout_web'] as const;

interface RemoteFlagConfig {
  enabled: boolean;
  rollout_pct: number;
  qa_user_ids: string[];
}

type RemoteFlagReason = 'qa_user' | 'disabled' | 'rollout_in' | 'rollout_out' | 'no_user' | 'no_config';

interface RemoteFlagResult {
  on: boolean;
  reason: RemoteFlagReason;
}

export function parseRemoteFlagConfig(value: unknown): RemoteFlagConfig | null {
  let raw: unknown = value;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const record = raw as Record<string, unknown>;
  const pct = typeof record.rollout_pct === 'number' && Number.isFinite(record.rollout_pct) ? record.rollout_pct : 0;
  return {
    enabled: record.enabled === true,
    rollout_pct: Math.min(100, Math.max(0, pct)),
    qa_user_ids: Array.isArray(record.qa_user_ids)
      ? record.qa_user_ids.filter((id): id is string => typeof id === 'string')
      : [],
  };
}

/** Stable bucket 0-99 for this flag and user (32-bit FNV-1a). */
export function remoteFlagBucket(flagKey: string, userId: string): number {
  let hash = 0x811c9dc5;
  const input = `${flagKey}:${userId}`;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0) % 100;
}

export function evaluateRemoteFlag(
  flagKey: string,
  config: RemoteFlagConfig | null,
  userId: string | null | undefined,
): RemoteFlagResult {
  if (!config) return { on: false, reason: 'no_config' };
  if (userId && config.qa_user_ids.includes(userId)) return { on: true, reason: 'qa_user' };
  if (!config.enabled) return { on: false, reason: 'disabled' };
  if (config.rollout_pct >= 100) return { on: true, reason: 'rollout_in' };
  if (!userId) return { on: false, reason: 'no_user' };
  return remoteFlagBucket(flagKey, userId) < config.rollout_pct
    ? { on: true, reason: 'rollout_in' }
    : { on: false, reason: 'rollout_out' };
}

/** True when the answer for a signed-out visitor can differ from a member's. */
export function remoteFlagDependsOnUser(config: RemoteFlagConfig | null): boolean {
  if (!config) return false;
  if (config.qa_user_ids.length > 0) return true;
  return config.enabled && config.rollout_pct > 0 && config.rollout_pct < 100;
}

export function remoteFlagQueryKey(key: string) {
  // Own cache entry: older hooks cache their parsed shapes under ['appConfig', key].
  return ['appConfig', 'remoteFlag', key] as const;
}

/** Keys whose answer the server put in the page, per query client. */
const serverSeededKeys = new WeakMap<QueryClient, Set<string>>();

/**
 * Seeds the flag cache with the raw app_config values the server read
 * (null for a missing row). A key the server could not read is left out and
 * loads in the browser as usual. Existing cache entries win.
 */
export function seedRemoteFlagCache(client: QueryClient, values: Record<string, unknown> | null | undefined): void {
  if (!values) return;
  const seeded = serverSeededKeys.get(client) ?? new Set<string>();
  serverSeededKeys.set(client, seeded);
  for (const [key, value] of Object.entries(values)) {
    seeded.add(key);
    if (client.getQueryData(remoteFlagQueryKey(key)) !== undefined) continue;
    client.setQueryData(remoteFlagQueryKey(key), parseRemoteFlagConfig(value));
  }
}

const noopSubscribe = () => () => {};

/**
 * False on the server and in the hydration render, true after, when `needed`.
 * When the answer is the same for every visitor, the server snapshot is true
 * too: React then has no snapshot change to fix after hydration, so it skips
 * the urgent re-render that would reach the route's still-loading Suspense
 * boundary and throw away the server HTML (minified error #421, #1301).
 */
function useHydrated(needed: boolean): boolean {
  return useSyncExternalStore(noopSubscribe, () => true, () => !needed);
}

async function fetchRemoteFlagConfig(key: string): Promise<RemoteFlagConfig | null> {
  const { data, error } = await supabase.from('app_config').select('value').eq('key', key).maybeSingle();
  if (error) throw error;
  return parseRemoteFlagConfig(data?.value ?? null);
}

export function useRemoteFlag(
  key: FlagKey,
  options: { enabled?: boolean } = {},
): RemoteFlagResult & { isLoading: boolean } {
  const { user, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const enabled = options.enabled ?? true;
  const config = useQuery({
    enabled,
    queryKey: remoteFlagQueryKey(key),
    queryFn: () => fetchRemoteFlagConfig(key),
    staleTime: 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
  const row = config.isError ? null : (config.data ?? null);
  const dependsOnUser = enabled && !config.isPending && remoteFlagDependsOnUser(row);
  // A page without the server's answer (the prerendered homepage) was loading
  // in its HTML. The browser can fetch the row before a lazy route hydrates,
  // so that hydration render must hold loading too, or the markup differs and
  // React throws the server HTML away (#1336).
  const answeredOnlyInBrowser =
    enabled && !config.isPending && !serverSeededKeys.get(queryClient)?.has(key);
  const hydrated = useHydrated(dependsOnUser || answeredOnlyInBrowser);
  if (!enabled) return { on: false, reason: 'no_config', isLoading: false };
  if (config.isPending) return { on: false, reason: 'no_config', isLoading: true };
  if (answeredOnlyInBrowser && !hydrated) return { on: false, reason: 'no_config', isLoading: true };
  if (dependsOnUser) {
    if (!hydrated) return { on: false, reason: 'no_config', isLoading: true };
    // Auth reads the session asynchronously; the stored session names the
    // member now, so a member in the rollout never sees the off state first.
    const userId = authLoading ? readStoredAuthUserId() : user?.id;
    return { ...evaluateRemoteFlag(key, row, userId), isLoading: false };
  }
  return { ...evaluateRemoteFlag(key, row, user?.id), isLoading: false };
}
